/**
 * Central API client for the 3D Color Time Tracker frontend.
 * Uses `fetch` with an optional auth token provider.
 * Zero-dependency, zero-regression, UI-spec compliant.
 */

/* -------------------------------------------------------------------------- */
/* Auth Token Provider                                                        */
/* -------------------------------------------------------------------------- */

let authTokenProvider = null;

/**
 * Register a function that returns a Promise<string | null>.
 * Your MSAL integration will call this once at app startup.
 */
export function setAuthTokenProvider(fn) {
  authTokenProvider = fn;
}

/* -------------------------------------------------------------------------- */
/* Internal Fetch Helper                                                      */
/* -------------------------------------------------------------------------- */

async function apiFetch(path, options = {}) {
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };

  // Attach bearer token if available
  if (authTokenProvider) {
    const token = await authTokenProvider();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }

  const response = await fetch(path, {
    ...options,
    headers,
    credentials: 'include', // Include cookies for backend session authentication
  });

  if (!response.ok) {
    let errorText = '';
    try {
      errorText = await response.text();
    } catch {
      /* ignore */
    }

    throw new Error(
      `API error ${response.status}: ${errorText || response.statusText}`
    );
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return null;
  }

  return response.json();
}

/* -------------------------------------------------------------------------- */
/* Auth / Identity                                                            */
/* -------------------------------------------------------------------------- */

export function getMe() {
  return apiFetch('/api/me');
}

/* -------------------------------------------------------------------------- */
/* Quotes                                                                     */
/* -------------------------------------------------------------------------- */

export function getQuotes(search = '') {
  const trimmed = (search || '').trim();
  const url = trimmed
    ? `/api/quotes?search=${encodeURIComponent(trimmed)}`
    : '/api/quotes';
  return apiFetch(url);
}

export function getQuote(quoteId) {
  if (!quoteId) throw new Error('getQuote: quoteId is required');
  return apiFetch(`/api/quotes/${encodeURIComponent(quoteId)}`);
}

/* -------------------------------------------------------------------------- */
/* Orders                                                                     */
/* -------------------------------------------------------------------------- */

export function getOrdersForQuote(quoteId) {
  if (!quoteId) throw new Error('getOrdersForQuote: quoteId is required');
  return apiFetch(
    `/api/orders?quoteNumber=${encodeURIComponent(quoteId)}`
  );
}

export function getOrder(orderId) {
  if (!orderId) throw new Error('getOrder: orderId is required');
  return apiFetch(`/api/orders/${encodeURIComponent(orderId)}`);
}

/* -------------------------------------------------------------------------- */
/* Jobs                                                                       */
/* -------------------------------------------------------------------------- */

export function getJobsForOrder(orderId) {
  if (!orderId) throw new Error('getJobsForOrder: orderId is required');
  return apiFetch(`/api/orders/${encodeURIComponent(orderId)}/jobs`);
}

export function getJob(jobId) {
  if (!jobId) throw new Error('getJob: jobId is required');
  return apiFetch(`/api/jobs/${encodeURIComponent(jobId)}`);
}

export function getRouting(jobId) {
  if (!jobId) throw new Error('getRouting: jobId is required');
  return apiFetch(`/api/jobs/${encodeURIComponent(jobId)}/routing`);
}

/* -------------------------------------------------------------------------- */
/* Job Time Tracking                                                          */
/* -------------------------------------------------------------------------- */

export function getJobTime(jobId) {
  if (!jobId) throw new Error('getJobTime: jobId is required');
  return apiFetch(`/api/jobs/${encodeURIComponent(jobId)}/time`);
}

export function startJobTime(jobId, operationId) {
  if (!jobId) throw new Error('startJobTime: jobId is required');
  if (!operationId) throw new Error('startJobTime: operationId is required');

  return apiFetch(
    `/api/jobs/${encodeURIComponent(jobId)}/time/start`,
    {
      method: 'POST',
      body: JSON.stringify({ operationId }),
    }
  );
}

export function pauseJobTime(jobId) {
  if (!jobId) throw new Error('pauseJobTime: jobId is required');
  return apiFetch(
    `/api/jobs/${encodeURIComponent(jobId)}/time/pause`,
    { method: 'POST' }
  );
}

export function resumeJobTime(jobId) {
  if (!jobId) throw new Error('resumeJobTime: jobId is required');
  return apiFetch(
    `/api/jobs/${encodeURIComponent(jobId)}/time/resume`,
    { method: 'POST' }
  );
}

export function completeJobTime(jobId) {
  if (!jobId) throw new Error('completeJobTime: jobId is required');
  return apiFetch(
    `/api/jobs/${encodeURIComponent(jobId)}/time/complete`,
    { method: 'POST' }
  );
}

/* -------------------------------------------------------------------------- */
/* Operations                                                                 */
/* -------------------------------------------------------------------------- */

export function getOperations() {
  return apiFetch('/api/operations');
}

/* -------------------------------------------------------------------------- */
/* Session (added later during SessionDashboard wiring)                       */
/* -------------------------------------------------------------------------- */

export async function getCurrentSession() {
  throw new Error('getCurrentSession is not implemented yet');
}

export async function submitSession(pendingIds) {
  void pendingIds;
  throw new Error('submitSession is not implemented yet');
}

/* -------------------------------------------------------------------------- */
/* Axios-like API Client Object                                               */
/* -------------------------------------------------------------------------- */

/**
 * Axios-style API client for components that prefer object syntax.
 * Usage: apiClient.get('/api/foo'), apiClient.post('/api/bar', { data })
 */
export const apiClient = {
  async get(url) {
    const response = await apiFetch(url);
    return { data: response };
  },

  async post(url, data) {
    const response = await apiFetch(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return { data: response };
  },

  async patch(url, data) {
    const response = await apiFetch(url, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
    return { data: response };
  },

  async delete(url) {
    const response = await apiFetch(url, {
      method: 'DELETE',
    });
    return { data: response };
  },
};

/* -------------------------------------------------------------------------- */
/* End of File                                                                */
/* -------------------------------------------------------------------------- */

